import { useMutation, useQuery, useQueryClient } from "react-query";
import { upsertPlanner, fetchPlannerById, fetchPlannerOverview } from "../api/plannerApi";

const getDayStartTimestamp = (referenceDate) => {
    const date = new Date(referenceDate);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};

const getStartOfWeekTimestamp = (referenceDate, weekOffset = 0) => {
    const base = new Date(referenceDate);
    const dayOfWeek = base.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    base.setDate(base.getDate() + diffToMonday + (weekOffset * 7));
    base.setHours(0, 0, 0, 0);
    return base.getTime();
};

const getStartOfMonthTimestamp = (referenceDate, monthOffset = 0) => {
    const date = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + monthOffset, 1);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};

export function usePlannerOverviews(token) {
    return useQuery({
        queryKey: ["planner-overview"],
        queryFn: async () => {
            const now = new Date();
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);

            const overviewEntries = await Promise.all([
                ["today", fetchPlannerOverview(token, "DAILY", getDayStartTimestamp(now))],
                ["tomorrow", fetchPlannerOverview(token, "DAILY", getDayStartTimestamp(tomorrow))],
                ["weekly", fetchPlannerOverview(token, "WEEKLY", getStartOfWeekTimestamp(now, 0))],
                ["nextweek", fetchPlannerOverview(token, "WEEKLY", getStartOfWeekTimestamp(now, 1))],
                ["monthly", fetchPlannerOverview(token, "MONTHLY", getStartOfMonthTimestamp(now))],
                ["nextmonth", fetchPlannerOverview(token, "MONTHLY", getStartOfMonthTimestamp(now, 1))],
            ].map(async ([periodKey, overviewPromise]) => [periodKey, await overviewPromise]));

            return Object.fromEntries(overviewEntries);
        },
        enabled: !!token,
        initialData: {},
    });
}

export function usePlannerById(token, plannerId) {
    return useQuery({
        queryKey: ["planner", plannerId],
        queryFn: () => fetchPlannerById(token, plannerId),
        enabled: !!token && !!plannerId,
    });
}

export function useUpsertPlanner(token) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (plannerData) => upsertPlanner(token, plannerData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["planner-overview"] });
            queryClient.invalidateQueries({ queryKey: ["planner"] });
        },
    });
}
